/**
 * Marine POI layer — Garmin ActiveCaptain (primary) + OpenStreetMap fallback
 * via /api/geo/pois. Popups load /api/geo/poi-details (ActiveCaptain summary).
 */
import React from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { distanceNm, bearingDeg } from './MobControl';
import PoiDetailsContent from './PoiDetailsContent';

const POI_STYLE = {
  marina: { emoji: '⚓', color: '#0ea5e9', label: 'Marina' },
  fuel: { emoji: '⛽', color: '#f59e0b', label: 'Fuel dock' },
  lock: { emoji: '🚧', color: '#8b5cf6', label: 'Lock' },
};

const iconCache = {};
function poiIcon(category) {
  if (!iconCache[category]) {
    const { emoji, color } = POI_STYLE[category] || POI_STYLE.marina;
    iconCache[category] = L.divIcon({
      className: 'marine-poi-marker',
      html: `<div style="
        width: 26px; height: 26px; border-radius: 50%;
        background: white; border: 2px solid ${color};
        display: flex; align-items: center; justify-content: center;
        font-size: 14px; box-shadow: 0 1px 4px rgba(0,0,0,0.35);
      ">${emoji}</div>`,
      iconSize: [26, 26],
      iconAnchor: [13, 13],
    });
  }
  return iconCache[category];
}

export default function MarinePoiLayer({
  pois,
  vesselPosition,
  poiDetails = {},
  onLookupDetails,
}) {
  if (!pois || pois.length === 0) return null;

  const requestDetails = (poi) => {
    if (onLookupDetails) onLookupDetails(poi);
  };

  return (
    <>
      {pois.map((poi) => {
        const style = POI_STYLE[poi.category] || POI_STYLE.marina;
        const extra = poiDetails[poi.id];
        const extraData = extra && !extra.loading ? extra : {};
        const loading = extra?.loading;
        const locality = extraData.displayName ? extraData.displayName.split(',')[0].trim() : null;
        const name = poi.name || extraData.name
          || (locality ? `${style.label} near ${locality}` : null);
        const address = poi.address || extraData.address || null;
        const phone = poi.phone || extraData.phone || null;
        const website = poi.website || extraData.website || null;
        const vhf = extraData.vhf || null;
        const dist = vesselPosition
          ? distanceNm(vesselPosition.latitude, vesselPosition.longitude, poi.latitude, poi.longitude)
          : null;
        const brg = vesselPosition
          ? bearingDeg(vesselPosition.latitude, vesselPosition.longitude, poi.latitude, poi.longitude)
          : null;

        return (
          <Marker
            key={poi.id}
            position={[poi.latitude, poi.longitude]}
            icon={poiIcon(poi.category)}
            opacity={poi.name ? 1 : 0.55}
            eventHandlers={{
              click: () => requestDetails(poi),
              popupopen: () => requestDetails(poi),
            }}
          >
            <Popup>
              <PoiDetailsContent
                name={name || `Unnamed ${style.label.toLowerCase()}`}
                categoryLabel={style.label}
                emoji={style.emoji}
                operator={poi.operator}
                rating={poi.rating}
                extraRating={extraData.rating}
                fuelTypes={poi.fuelTypes}
                address={address}
                phone={phone}
                website={website}
                vhf={vhf}
                notes={extraData.notes}
                distanceNm={dist}
                bearingDeg={brg}
                loading={loading}
                unavailable={extra && !loading}
              />
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}
